const BASE_URL = "https://s3service.sconexsoft.com/s3bucket/presigned-url";
const BUCKET_NAME = "imageinsight-web-assets";
const VIDEO_PATH_PREFIX = "";
const VIDEO_CACHE_KEY = "imageinsight-video-cache-v1";
const CACHE_NAMESPACE = "/__imageinsight_video_cache__";

const presignedUrlCache = new Map();
const videoUrlCache = new Map();
const downloadPromises = new Map();

function buildPresignedRequestUrl(fileName) {
  const filePath = `${VIDEO_PATH_PREFIX}${fileName}`;
  return `${BASE_URL}?fileName=${encodeURIComponent(
    filePath
  )}&bucketName=${BUCKET_NAME}&operation=download`;
}

function getAuthToken() {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem("authToken");
}

function hasCacheSupport() {
  return typeof window !== "undefined" && "caches" in window;
}

async function getPresignedUrl(fileName, retry = 0) {
  if (presignedUrlCache.has(fileName)) {
    return presignedUrlCache.get(fileName);
  }

  const token = getAuthToken();
  const requestUrl = buildPresignedRequestUrl(fileName);

  try {
    const response = await fetch(requestUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(
        `Failed to get presigned URL for ${fileName}: ${response.status} ${response.statusText} ${text}`
      );
    }

    const bodyText = (await response.text()).trim();
    if (!bodyText.startsWith("http")) {
      throw new Error(
        `Invalid presigned URL format for ${fileName}: ${bodyText.substring(
          0,
          150
        )}`
      );
    }

    presignedUrlCache.set(fileName, bodyText);
    return bodyText;
  } catch (error) {
    if (retry < 2) {
      await new Promise((resolve) =>
        setTimeout(resolve, 500 * (retry + 1))
      );
      return getPresignedUrl(fileName, retry + 1);
    }
    throw error;
  }
}

async function getCachedVideoUrl(fileName) {
  if (!hasCacheSupport()) return null;
  const cache = await caches.open(VIDEO_CACHE_KEY);
  const cacheKey = `${CACHE_NAMESPACE}/${fileName}`;
  const cachedResponse = await cache.match(cacheKey);
  if (!cachedResponse) return null;
  const blob = await cachedResponse.blob();
  const objectUrl = URL.createObjectURL(blob);
  videoUrlCache.set(fileName, objectUrl);
  return objectUrl;
}

async function saveVideoToCache(fileName, blob) {
  if (!hasCacheSupport()) return;
  const cache = await caches.open(VIDEO_CACHE_KEY);
  const cacheKey = `${CACHE_NAMESPACE}/${fileName}`;
  const response = new Response(blob, {
    headers: {
      "Content-Type": blob.type || "video/mp4",
      "Content-Length": String(blob.size),
    },
  });
  await cache.put(cacheKey, response);
}

async function downloadVideo(fileName) {
  if (videoUrlCache.has(fileName)) {
    return videoUrlCache.get(fileName);
  }

  if (downloadPromises.has(fileName)) {
    return downloadPromises.get(fileName);
  }

  const downloadPromise = (async () => {
    const cached = await getCachedVideoUrl(fileName);
    if (cached) {
      return cached;
    }

    const presignedUrl = await getPresignedUrl(fileName);
    const response = await fetch(presignedUrl, {
      method: "GET",
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(
        `Failed to download video ${fileName}: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    await saveVideoToCache(fileName, blob);
    const objectUrl = URL.createObjectURL(blob);
    videoUrlCache.set(fileName, objectUrl);
    return objectUrl;
  })()
    .catch((error) => {
      videoUrlCache.delete(fileName);
      throw error;
    })
    .finally(() => {
      downloadPromises.delete(fileName);
    });

  downloadPromises.set(fileName, downloadPromise);
  return downloadPromise;
}

export async function getVideoSource(fileName) {
  return downloadVideo(fileName);
}

export async function preloadVideos(fileNames) {
  for (const file of fileNames) {
    try {
      await downloadVideo(file);
    } catch (error) {
      console.error("[VIDEO SERVICE] Failed to preload", file, error);
    }
  }
}

export function clearVideoCache() {
  videoUrlCache.forEach((url) => URL.revokeObjectURL(url));
  videoUrlCache.clear();
  downloadPromises.clear();
  if (hasCacheSupport()) {
    caches.delete(VIDEO_CACHE_KEY);
  }
}

export function isVideoCached(fileName) {
  return videoUrlCache.has(fileName);
}

